import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUpRightIcon } from 'lucide-react';
import { BrandIcon } from './BrandIcon';

// FAR-skinned Spotify card with REAL in-page playback (Ryan, Aug 31:
// "songs play when you click them, not open a new window").
//
// WHY IT IS BUILT THIS WAY - do not "simplify" it back:
//  * Spotify audio can ONLY come out of Spotify's own player. There is no public
//    endpoint that streams a track, and the Web Playback SDK needs each visitor to
//    log in with Spotify Premium - useless for a public site.
//  * The one thing that works for an anonymous visitor is Spotify's EMBED, driven
//    by the official IFrame API (open.spotify.com/embed/iframe-api/v1), which gives
//    us loadUri() + play() on a controller. So the embed stays - but as an 80px
//    DOCK at the bottom of the card that only appears once someone presses a track.
//    The clunky 152px+ embed header Ryan rejected never renders.
//  * ⚠️ LIMIT WE CANNOT ENGINEER AROUND: a visitor who is NOT signed in to Spotify
//    in that browser hears a 30-second preview, and Spotify does not count those as
//    streams. Signed-in visitors hear the full track and it counts.
//
// HEADER CIRCLE = THE PLATFORM MARK (Ryan, Aug 31: "add the Spotify logo in the
// icon"). The circle carries the Spotify logo, so the duplicate green dot that used
// to sit on the eyebrow line was removed. Pressing it starts the playlist at track 1;
// pause lives in the dock, which is Spotify's own player.
//
// The tracklist comes from a Make webhook that reads the playlist through the FAR
// Spotify connection (Make scenario 6103594 / hook 2754081, ~70 tracks in 0.8s).
// If it is ever paused the card falls back to an "Open on Spotify" link.
// Track URIs are derived from each track's open.spotify.com URL - no Make change.

const FEED = 'https://hook.us2.make.com/hb3yxjaawwmjoixi9eww6m4mywcmvgvn';
const IFRAME_API = 'https://open.spotify.com/embed/iframe-api/v1';
declare global {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  interface Window {
    onSpotifyIframeApiReady?: (api: any) => void;
    __spotifyIframeApi?: any;
  }
}

// Resolve the IFrame API once for the whole page, however many cards use it.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let apiPromise: Promise<any> | null = null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function loadIframeApi(): Promise<any> {
  if (!apiPromise) {
    apiPromise = new Promise((resolve, reject) => {
      if (window.__spotifyIframeApi) return resolve(window.__spotifyIframeApi);
      const prev = window.onSpotifyIframeApiReady;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      window.onSpotifyIframeApiReady = (api: any) => {
        window.__spotifyIframeApi = api;
        if (typeof prev === 'function') prev(api);
        resolve(api);
      };
      const s = document.createElement('script');
      s.src = IFRAME_API;
      s.async = true;
      s.onerror = () => reject(new Error('spotify iframe api blocked'));
      document.head.appendChild(s);
      setTimeout(() => reject(new Error('spotify iframe api timeout')), 12000);
    });
  }
  return apiPromise;
}
const fmt = (ms: number) => {
  if (!ms) return '';
  const m = Math.floor(ms / 60000);
  const s = Math.floor(ms % 60000 / 1000);
  return `${m}:${String(s).padStart(2, '0')}`;
};

// https://open.spotify.com/track/<id>?si=... -> spotify:track:<id>
const uriFromUrl = (url: string) => {
  const m = /open\.spotify\.com\/track\/([A-Za-z0-9]+)/.exec(url || '');
  return m ? `spotify:track:${m[1]}` : '';
};
type Track = {
  idx: number;
  title: string;
  artists: string;
  duration: number;
  url: string;
  uri: string;
  art: string;
};
type Props = {
  playlistUrl: string;
  title: string;
  eyebrow: string;
  accent: string;
};
export function SpotifyPlayer({
  playlistUrl,
  title,
  eyebrow,
  accent
}: Props) {
  const [tracks, setTracks] = useState<Track[]>([]);
  const [failed, setFailed] = useState(false);
  const [cur, setCur] = useState<number | null>(null);
  const [docked, setDocked] = useState(false);
  const hostRef = useRef<HTMLDivElement | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ctrlRef = useRef<any>(null);
  const pendingRef = useRef<string>('');
  const readyRef = useRef(false);

  // ---- tracklist ---------------------------------------------------------
  useEffect(() => {
    let dead = false;
    const ctl = new AbortController();
    const to = setTimeout(() => ctl.abort(), 12000);
    fetch(FEED, {
      signal: ctl.signal
    }).then(r => r.ok ? r.json() : Promise.reject(new Error(String(r.status)))).then(d => {
      if (dead) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rows: Track[] = ((d?.items || []) as any[]).map((it, i) => {
        const t = it?.item || it?.track;
        if (!t?.name) return null;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const imgs = (t.album?.images || []).slice().sort((a: any, b: any) => (a.width || 0) - (b.width || 0));
        const url = t.external_urls?.spotify || '';
        return {
          idx: i,
          title: t.name as string,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          artists: (t.artists || []).map((a: any) => a.name).join(', '),
          duration: t.duration_ms || 0,
          url: url || playlistUrl,
          uri: t.uri || uriFromUrl(url),
          art: imgs[0]?.url || ''
        };
      }).filter(Boolean) as Track[];
      if (rows.length) setTracks(rows);else setFailed(true);
    }).catch(() => !dead && setFailed(true)).finally(() => clearTimeout(to));
    return () => {
      dead = true;
      ctl.abort();
    };
  }, [playlistUrl]);

  // ---- the dock: Spotify's own player, created on first press ------------
  const playUri = useCallback((uri: string, idx: number) => {
    if (!uri) return;
    setCur(idx);
    setDocked(true);
    pendingRef.current = uri;
    if (ctrlRef.current && ctrlRef.current !== 'pending' && readyRef.current) {
      ctrlRef.current.loadUri(uri);
      // loadUri does not autostart; play() lands once the new uri is loaded
      setTimeout(() => {
        try {
          ctrlRef.current?.play();
        } catch {/* ignore */}
      }, 350);
      return;
    }
    if (ctrlRef.current) return; // still spinning up; the ready handler plays it

    ctrlRef.current = 'pending';
    loadIframeApi().then(api => {
      if (!hostRef.current) return;
      api.createController(hostRef.current, {
        uri: pendingRef.current,
        width: '100%',
        height: 80
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (ctrl: any) => {
        ctrlRef.current = ctrl;
        ctrl.addListener('ready', () => {
          readyRef.current = true;
          try {
            ctrl.play();
          } catch {/* user gesture already given */}
        });
      });
    }).catch(() => {
      ctrlRef.current = null;
      setDocked(false);
    });
  }, []);
  return <div className="scp flex min-h-0 min-w-0 flex-1 flex-col gap-1.5" data-id="element-381" data-mp="1qyu064vnxcmb">
      {/* ONE header band: platform mark, name over collection line, open. */}
      <div className="flex items-center gap-2" data-id="element-382" data-mp="1myjxp272x4c7">
        <button type="button" aria-label="Play the playlist on Spotify" onClick={() => {
        const t = tracks[0];
        if (t) playUri(t.uri, t.idx);
      }} className="flex h-8 w-8 flex-none items-center justify-center rounded-full text-white shadow-neon transition-transform duration-150 ease-expo hover:scale-105 active:scale-95" style={{
        background: 'linear-gradient(135deg, #37e5ff, #8b5cff)'
      }} data-id="element-383" data-mp="2jlet39rsdwrz">
          <BrandIcon name="spotify" className="h-[1.05rem] w-[1.05rem]" data-id="element-384" data-mp="32esi6y8tj8zo" />
        </button>
        <div className="min-w-0 flex-1" data-id="element-385" data-mp="2xk4rcsqce2hx">
          <p className="truncate text-[0.8rem] font-semibold leading-tight text-white" data-id="element-386" data-mp="3h9q8dlidskmc">
            {failed ? 'Open the playlist on Spotify' : cur !== null && tracks[cur] ? tracks[cur].title : title}
          </p>
          <p className={`mt-0.5 truncate font-tech text-[0.55rem] font-semibold uppercase tracking-[0.22em] ${accent}`} data-id="element-387" data-mp="37d50msez8vet">
            {eyebrow}{tracks.length ? ` · ${tracks.length} tracks` : ''}
          </p>
        </div>
        <a href={playlistUrl} target="_blank" rel="noopener noreferrer" aria-label="Open on Spotify" className="flex h-6 w-6 flex-none items-center justify-center rounded-md border border-white/10 text-white/50 transition-colors duration-200 hover:border-cyan/50 hover:text-cyan" data-id="element-388" data-mp="3vwis8k91vk3">
          <ArrowUpRightIcon className="h-3 w-3" aria-hidden="true" data-id="element-389" data-mp="2enek6cpvhhfj" />
        </a>
      </div>
      {/* the rail IS the divider - the list carries no border of its own */}
      <div className="h-[2px] flex-none overflow-hidden rounded-full bg-white/10" data-id="element-390" data-mp="2jnsu60b1b1g7">
        <i className="block h-full w-0" data-id="element-391" data-mp="11nbb7oiqa0gj" />
      </div>

      {/* tracklist - a press plays it in the dock below, never a new window */}
      <ol className="scp-list min-h-0 flex-1 list-none overflow-y-auto p-0" data-id="element-392" data-mp="ws5v56c0bg0d">
        {tracks.map(t => <li key={t.idx} className="border-b border-white/5" data-id="element-393" data-mp="16nzaexh41nlu">
            <button type="button" onClick={() => playUri(t.uri, t.idx)} className={`flex w-full items-center gap-2 px-1 py-1 text-left transition-colors duration-150 hover:bg-violet/10 ${t.idx === cur ? 'bg-violet/10' : ''}`} data-id="element-394" data-mp="16oqwnhoti8zg">
              <span className={`w-4 flex-none font-tech text-[0.54rem] ${t.idx === cur ? 'text-cyan' : 'text-white/35'}`} data-id="element-395" data-mp="rri2kg84qr41">
                {String(t.idx + 1).padStart(2, '0')}
              </span>
              {t.art ? <img src={t.art} alt="" loading="lazy" className="h-5 w-5 flex-none rounded-[3px] object-cover opacity-90" data-id="element-396" data-mp="3vdp58zkk9snp" /> : null}
              <span className={`min-w-0 flex-1 truncate text-[0.72rem] leading-snug ${t.idx === cur ? 'text-cyan' : 'text-white/75'}`} data-id="element-397" data-mp="cvs1ltssmupq">
                {t.title}
                <span className={t.idx === cur ? 'text-cyan/60' : 'text-white/35'} data-id="element-398" data-mp="7um8vvz4v7g7"> · {t.artists}</span>
              </span>
              <span className="flex-none font-tech text-[0.56rem] text-white/35" data-id="element-399" data-mp="3v1eq6zo2636a">{fmt(t.duration)}</span>
            </button>
          </li>)}
        {!tracks.length && !failed ? <li className="px-1 py-1 text-[0.72rem] text-white/40" data-id="element-400" data-mp="3lblii9r1e87b">Loading tracklist…</li> : null}
        {failed ? <li className="px-1 py-2 text-[0.72rem] text-white/40" data-id="element-401" data-mp="3gbh2slxydhi5">
            <a href={playlistUrl} target="_blank" rel="noopener noreferrer" className="text-cyan" data-id="element-402" data-mp="3cane3qcoryo7">Open Future Astronaut Records on Spotify ↗</a>
          </li> : null}
      </ol>

      {/* THE DOCK - Spotify's real player, 80px, only after a press */}
      <div className={`flex-none overflow-hidden rounded-lg transition-[height,opacity,margin] duration-300 ease-expo ${docked ? 'mt-1 h-20 opacity-100' : 'h-0 opacity-0'}`} data-id="element-403" data-mp="32czf2o81s508">
        <div ref={hostRef} data-id="element-404" data-mp="1ms88zs7iklow" />
      </div>
    </div>;
}